"use client";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { cn } from "@/app/utils/cn";
import StoreSearch from "./StoreSearch";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import { StoreTab } from "@/app/lib/features/store/store.types";
import { trackGeneric } from "@/app/lib/tracking/interactions";

interface StoreTabsProps {
  storeName: string;
  searchPlaceholder: string;
  tabs: StoreTab[];
  sellerSlug: string;
  activeTabKey: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
}

const StoreTabs = ({
  storeName,
  searchPlaceholder,
  tabs,
  sellerSlug,
  activeTabKey,
  searchValue,
  onSearchChange,
}: StoreTabsProps) => {
  const router = useRouter();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleTabClick = (tab: StoreTab) => {
    trackGeneric(`store_tab_${tab.key_2 || tab.key}`, "click");
    if (tab.key_2 && tab.key_2 !== "home") {
      router.push(`/magaza/${sellerSlug}/${tab.key_2}`);
    } else {
      router.push(`/magaza/${sellerSlug}`);
    }
  };

  return (
    <div
      className={cn(
        "sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm transition-all duration-300",
        isScrolled ? "mt-0" : "mt-5",
      )}
    >
      <div className="container-main">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-6 py-3 md:py-4">
          <div className="flex-1 overflow-hidden">
            <div className="hidden md:flex items-center gap-8">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabClick(tab)}
                  className={cn(
                    "pb-1 font-semibold text-base transition-colors relative whitespace-nowrap cursor-pointer",
                    activeTabKey === tab.key
                      ? "text-main"
                      : "text-gray-600 hover:text-gray-900",
                  )}
                >
                  {tab.title}
                  {activeTabKey === tab.key && (
                    <div className="absolute -bottom-4 left-0 right-0 h-0.5 bg-main" />
                  )}
                </button>
              ))}
            </div>

            <div className="md:hidden">
              <Swiper
                spaceBetween={16}
                slidesPerView="auto"
                freeMode={true}
                modules={[FreeMode]}
              >
                {tabs.map((tab) => (
                  <SwiperSlide key={tab.key} className="w-auto!">
                    <button
                      onClick={() => handleTabClick(tab)}
                      className={cn(
                        "pb-1 font-semibold text-sm transition-colors relative whitespace-nowrap cursor-pointer",
                        activeTabKey === tab.key
                          ? "text-main"
                          : "text-gray-600 hover:text-gray-900",
                      )}
                    >
                      {tab.title}
                      {activeTabKey === tab.key && (
                        <div className="absolute -bottom-3 left-0 right-0 h-0.5 bg-main" />
                      )}
                    </button>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>

          {(activeTabKey.includes("all_products") || activeTabKey.includes("deals")) && (
            <div className="w-full md:w-auto md:max-w-sm">
              <StoreSearch
                storeName={storeName}
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={onSearchChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreTabs;
